import {useMap, Marker, Popup} from 'react-leaflet';
import L, {LatLngBoundsExpression, LatLngExpression} from 'leaflet';
import 'leaflet-routing-machine';
import {
  createRef,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './style.css';
import {Order} from 'types';
import useStore from '../../store';
import {get, isString} from 'lodash';
import {addressToString} from 'helpers/address';
import styles from './RoutingMachine.module.scss';
import {Popup as LeafletPopup} from 'leaflet';
import classNames from 'classnames';
import truck_icon from '../../icons/truck.svg';
import useDriverTracking from './useDriverTracking';

type UniqueMarker = {
  lat: number;
  lng: number;
  orders: Order[];
  label: string;
  hasMultiple: boolean;
  firstOrder: Order;
  popupRefKey: string;
};

const returnIcon = (
  index: number | string = 1,
  orders?: Order[],
  position?: 'start' | 'end'
) => {
  let classNames = [styles.marker];
  let text = String(index);

  if (position === 'start') {
    classNames.push(styles.start);
    text = '';
  } else if (position === 'end') {
    classNames.push(styles.end);
    text = '';
  } else {
    classNames.push(getMarkerStyleByStatus(orders));
  }

  return new L.DivIcon({
    className: 'leaflet-div-icon',
    html: `<div class="my-div-icon ${classNames.join(' ')}">${text}</div>`,
  });
};

const returnDriverIcon = () => {
  return new L.DivIcon({
    className: 'leaflet-div-icon-driver',
    html: `<div class="my-div-icon-driver"><img src="${truck_icon}"/></div>`,
  });
};

const isSkipped = (status: string) => status === 'cancelled';
const getMarkerStyleByStatus = (orders?: Order[]) => {
  if (!orders) return styles.default;

  if (orders.some((order) => order.status === 'completed')) {
    return styles.completed;
  } else if (
    orders.every((order) => order.status === ('cancelled' as 'canceled'))
  ) {
    return styles.skipped;
  } else if (orders.every((order) => order.is_canceled)) {
    return styles.skipped;
  } else {
    return styles.default;
  }
};

type Props = {
  display: boolean;
  orders: Order[];
  // routeType?: 'car' | 'bike';
  // isHistory?: boolean;
  markerClick?: (id: string) => void;
  polylines?: LatLngExpression[];
  start_location: {
    name: string;
    lat: number;
    lng: number;
  };
  openedPopup?: {
    index: number;
    open: boolean;
    refKey: string;
  };
  end_location: {
    name: string;
    lat: number;
    lng: number;
  };
  driver_id?: number;
  shouldDrawRoute?: boolean;
};

const RoutingMachine = ({
  display,
  orders,
  start_location,
  end_location,
  markerClick,
  polylines,
  openedPopup,
  driver_id,
  shouldDrawRoute,
}: Props) => {
  const map = useMap();
  const directionRef = useRef<any>(null);
  const {location} = useDriverTracking(driver_id);
  const markerPopupRefs = useRef<{[id: string]: React.RefObject<LeafletPopup>}>(
    {}
  );
  const setDistance = useStore(useCallback((state) => state.setDistance, []));
  const [points, setPoints] = useState<any[]>([]);
  const isMappingJobs = display && polylines?.length === 0;
  // Helper function to group orders by location
  const groupOrdersByLocation = useCallback((orders: Order[]) => {
    const locationGroups: {[key: string]: Order[]} = {};

    orders.forEach((order, index) => {
      const lat = get(order, 'delivery_address.latitude', 0);
      const lng = get(order, 'delivery_address.longitude', 0);
      const locationKey = `${lat},${lng}`;

      if (!locationGroups[locationKey]) {
        locationGroups[locationKey] = [];
      }
      locationGroups[locationKey].push({...order, countNumber: index + 1});
    });

    return locationGroups;
  }, []);
  const drawWithRouting = useCallback(
    (waypoints: any[]) => {
      // Filter out waypoints with invalid coordinates
      const validWaypoints = waypoints.filter(
        (item) =>
          typeof item.lat === 'number' &&
          typeof item.lng === 'number' &&
          !isNaN(item.lat) &&
          !isNaN(item.lng)
      );

      // Need at least 2 waypoints for routing
      if (validWaypoints.length < 2) {
        return;
      }

      // @ts-ignore
      directionRef.current = new L.Routing.control({
        waypoints: validWaypoints.map((item) => L.latLng(item.lat, item.lng)),
        lineOptions: {
          styles: [
            {
              color: '#4000FF',
              opacity: 0.8,
              weight: 4,
            },
          ],
        },
        createMarker: function () {
          return null;
        },
        addWaypoints: false,
        draggableWaypoints: false,
        //@ts-ignore
        router: L.Routing.osrmv1({
          serviceUrl: `https://routing.openstreetmap.de/routed-car/route/v1`,
        }),
      }).addTo(map);
      map.invalidateSize();
    },
    [map]
  );
  const setupDirections = useCallback(
    (orders: Order[]) => {
      if (orders.length < 1 || !start_location.name) {
        setDistance(0);
        return;
      }
      const normalizedPoints: any[] = [
        start_location,
        ...orders.map((item) => ({
          ...item,
          lat: !isString(item.delivery_address)
            ? item.delivery_address?.latitude
            : 0,
          lng: !isString(item.delivery_address)
            ? item.delivery_address?.longitude
            : 0,

          name: isString(item.delivery_address)
            ? item.delivery_address
            : item.delivery_address?.display_address ||
              addressToString(item.delivery_address) +
                (item.unit_address ? `Unit# ${item.unit_address}` : ''),
        })),
        end_location,
      ];

      let distance = 0;

      normalizedPoints.forEach((point, index) => {
        const currentLat = point.lat;
        const currentLng = point.lng;

        // Skip if current point has invalid coordinates
        if (
          typeof currentLat !== 'number' ||
          typeof currentLng !== 'number' ||
          isNaN(currentLat) ||
          isNaN(currentLng)
        ) {
          return;
        }

        if (index < normalizedPoints.length - 1) {
          const nextPoint = normalizedPoints[index + 1];
          const nextLat = nextPoint.lat;
          const nextLng = nextPoint.lng;

          // Skip if next point has invalid coordinates
          if (
            typeof nextLat === 'number' &&
            typeof nextLng === 'number' &&
            !isNaN(nextLat) &&
            !isNaN(nextLng)
          ) {
            distance += map.distance(
              [currentLat, currentLng],
              [nextLat, nextLng]
            );
          }
        } else {
          const firstPoint = normalizedPoints[0];
          const firstLat = firstPoint.lat;
          const firstLng = firstPoint.lng;

          // Skip if first point has invalid coordinates
          if (
            typeof firstLat === 'number' &&
            typeof firstLng === 'number' &&
            !isNaN(firstLat) &&
            !isNaN(firstLng)
          ) {
            distance += map.distance(
              [currentLat, currentLng],
              [firstLat, firstLng]
            );
          }
        }
      });

      setDistance(distance);

      setPoints(normalizedPoints);
      if (isMappingJobs && shouldDrawRoute) {
        drawWithRouting(normalizedPoints);
      }
    },
    [
      start_location,
      end_location,
      setDistance,
      isMappingJobs,
      map,
      drawWithRouting,
      shouldDrawRoute,
    ]
  );

  const drawPolylines = useCallback(() => {
    if (polylines && !!polylines.length) {
      directionRef.current = L.polyline(polylines, {
        color: '#1A6AE5',
      })?.addTo(map);
      map?.fitBounds(polylines as LatLngBoundsExpression);
      map?.invalidateSize();
    }
  }, [map, polylines]);

  useEffect(() => {
    if (!map) return;
    // Clear all polylines before redraw. This is not perfect. This should be optimized.
    if (directionRef.current) {
      map.removeControl(directionRef.current);
    }
    setupDirections(orders);
    if (!isMappingJobs && shouldDrawRoute) drawPolylines();
  }, [
    setupDirections,
    drawPolylines,
    orders,
    map,
    isMappingJobs,
    shouldDrawRoute,
  ]);

  useEffect(() => {
    if (openedPopup?.open && openedPopup?.refKey) {
      const ref = markerPopupRefs.current[openedPopup.refKey];
      if (ref && ref.current) {
        ref.current.openOn(map);
      }
    }
    if (!openedPopup?.open && openedPopup?.refKey) {
      const ref = markerPopupRefs.current[openedPopup.refKey];
      if (ref && ref.current) {
        ref.current.close();
      }
    }
  }, [openedPopup, map]);
  // Group orders by location for markers
  const locationGroups = groupOrdersByLocation(
    points.slice(1, -1) // Remove start and end locations
  );
  // Create unique markers array
  const uniqueMarkers: UniqueMarker[] = useMemo(
    () =>
      Object.entries(locationGroups).map(([locationKey, ordersAtLocation]) => {
        const [lat, lng] = locationKey.split(',').map(Number);
        const orderCount = ordersAtLocation.length;

        // calculate marker label
        let markerLabel;
        if (orderCount === 1) {
          markerLabel = String(ordersAtLocation[0].countNumber);
        } else if (orderCount === 2) {
          markerLabel = ordersAtLocation
            .map((order) => order.countNumber)
            .join('/');
        } else {
          markerLabel =
            ordersAtLocation
              .slice(0, 2)
              .map((order) => order.countNumber)
              .join('/') + '..';
        }

        return {
          lat,
          lng,
          orders: ordersAtLocation,
          label: markerLabel,
          hasMultiple: orderCount > 1,
          firstOrder: ordersAtLocation[0],
          popupRefKey: locationKey,
        };
      }),
    [locationGroups]
  );

  if (!display || !start_location.name) {
    return null;
  }

  return (
    <Fragment>
      {!!location?.latitude && !!location?.longitude && (
        <Marker
          icon={returnDriverIcon()}
          position={[location?.latitude, location?.longitude]}
        ></Marker>
      )}
      {/* Start location marker */}
      {points.length > 0 && (
        <Marker
          key="start-marker"
          icon={returnIcon(0, undefined, 'start')}
          position={[points[0].lat, points[0].lng]}
        />
      )}
      {/* End location marker */}
      {points.length > 0 && (
        <Marker
          key="end-marker"
          icon={returnIcon(0, undefined, 'end')}
          position={[
            points[points.length - 1].lat,
            points[points.length - 1].lng,
          ]}
        />
      )}
      {uniqueMarkers.map((marker, index) => {
        if (!markerPopupRefs.current[marker.popupRefKey]) {
          markerPopupRefs.current[marker.popupRefKey] =
            createRef<LeafletPopup>();
        }
        return (
          <Marker
            key={`${marker.lat}-${marker.lng}-${index}`}
            icon={returnIcon(marker.label, marker.orders)}
            eventHandlers={{
              click: () => {
                markerClick && markerClick(marker.popupRefKey);
              },
            }}
            position={[marker.lat, marker.lng]}
          >
            <Popup ref={markerPopupRefs.current[marker.popupRefKey]}>
              <PopupContent marker={marker} />
            </Popup>
          </Marker>
        );
      })}
    </Fragment>
  );
};

const PopupContent = ({marker}: {marker: UniqueMarker}) => {
  const isMultiple = marker.hasMultiple;
  const isLastItem = (orderIndex: number) => {
    return orderIndex === marker.orders.length - 1;
  };
  return (
    <Fragment>
      {isMultiple ? (
        marker.orders.map((order: Order, orderIdx: number) => (
          <div
            key={orderIdx}
            className={classNames(!isLastItem(orderIdx) && styles.borderBottom)}
          >
            <span
              className={classNames(
                styles.stopLabel,
                isSkipped(order.status) && styles.skippedLabel
              )}
            >
              Stop {order.countNumber}
            </span>
            <br />
            <span>
              Customer - {get(order, 'customer.user.first_name', 'N/A')}{' '}
              {get(order, 'customer.user.last_name', 'N/A')}
            </span>
            <br />
            <span>Address - {get(order, 'name', '')}</span>
          </div>
        ))
      ) : (
        <div>
          <span
            className={classNames(
              styles.stopLabel,
              isSkipped(marker.firstOrder.status) && styles.skippedLabel
            )}
          >
            Stop {marker.firstOrder.countNumber}
          </span>
          <br />
          <span>
            Customer -{' '}
            {get(marker.firstOrder, 'customer.user.first_name', 'N/A')}{' '}
            {get(marker.firstOrder, 'customer.user.last_name', 'N/A')}
          </span>
          <br />
          <span>Address - {get(marker.firstOrder, 'name', '')}</span>
        </div>
      )}
    </Fragment>
  );
};

export default RoutingMachine;
